/**
 * SearchModal — Global Cmd+K / Ctrl+K intelligent search dialog.
 * Features autocomplete, recent searches, popular keywords, and instant product results.
 */

import { useState, useEffect } from "react";
import { Link, useNavigate } from "@tanstack/react-router";
import { Search, X, History, TrendingUp, ArrowRight } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useLiveProducts } from "@/features/products/hooks/use-live-products";
import { type Product, getProductSlug } from "@/lib/products";
import { formatPrice } from "@/components/forms/price-display";

const POPULAR_SEARCHES = ["Bonsai", "Bougainvillea", "Topiary", "Lotus", "Sunflower", "Blossom"];
const RECENT_KEY = "verdant-recent-searches";

type SearchModalProps = {
  open: boolean;
  onClose: () => void;
};

export function SearchModal({ open, onClose }: SearchModalProps) {
  const [query, setQuery] = useState("");
  const [recentSearches, setRecentSearches] = useState<string[]>([]);
  const { products: catalog } = useLiveProducts();
  const navigate = useNavigate();

  // Load recent searches
  useEffect(() => {
    try {
      const raw = localStorage.getItem(RECENT_KEY);
      if (raw) setRecentSearches(JSON.parse(raw));
    } catch {
      // Ignore
    }
  }, []);

  const saveRecentSearch = (term: string) => {
    if (!term.trim()) return;
    const next = [term, ...recentSearches.filter((s) => s !== term)].slice(0, 5);
    setRecentSearches(next);
    try {
      localStorage.setItem(RECENT_KEY, JSON.stringify(next));
    } catch {
      // Ignore
    }
  };

  const results: Product[] = query.trim()
    ? catalog.filter(
        (p) =>
          (p.name || "").toLowerCase().includes(query.toLowerCase()) ||
          (p.category || "").toLowerCase().includes(query.toLowerCase()) ||
          (p.note || "").toLowerCase().includes(query.toLowerCase()),
      )
    : [];

  const handleSelectSearch = (term: string) => {
    saveRecentSearch(term);
    onClose();
    navigate({
      to: "/shop",
      search: { q: term } as unknown as { category?: string; search?: string },
    });
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-xl p-0 overflow-hidden">
        <DialogHeader className="sr-only">
          <DialogTitle>Search Botanicals</DialogTitle>
        </DialogHeader>

        {/* Search input header */}
        <div className="flex items-center border-b px-4 py-3">
          <Search className="h-5 w-5 text-muted-foreground mr-3 shrink-0" />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search botanicals, trees, pots or rooms..."
            className="w-full bg-transparent text-sm outline-none placeholder:text-muted-foreground/60"
            autoFocus
          />
          {query && (
            <button
              onClick={() => setQuery("")}
              className="p-1 text-muted-foreground hover:text-foreground"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>

        <div className="max-h-[60vh] overflow-y-auto p-4 space-y-6">
          {/* Instant Product Results */}
          {query.trim() !== "" ? (
            <div>
              <p className="eyebrow mb-3">Results ({results.length})</p>
              {results.length === 0 ? (
                <p className="text-sm text-muted-foreground py-6 text-center">
                  No matching botanicals found for "{query}".
                </p>
              ) : (
                <ul className="divide-y">
                  {results.map((product) => (
                    <li key={product.id}>
                      <Link
                        to="/product/$id"
                        params={{ id: getProductSlug(product) }}
                        onClick={() => {
                          saveRecentSearch(product.name);
                          onClose();
                        }}
                        className="flex items-center justify-between py-3 group hover:bg-secondary/40 px-2 rounded transition-colors"
                      >
                        <div className="flex items-center gap-3">
                          <img
                            src={product.image}
                            alt={product.name}
                            className="h-12 w-10 object-cover bg-stone rounded-sm"
                          />
                          <div>
                            <p className="text-sm font-medium group-hover:text-primary transition-colors">
                              {product.name}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              {product.category} · {product.note}
                            </p>
                          </div>
                        </div>
                        <span className="text-xs font-display tabular-nums">
                          {formatPrice(product.price)}
                        </span>
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ) : (
            <>
              {/* Popular Searches */}
              <div>
                <p className="eyebrow flex items-center gap-1.5 mb-3">
                  <TrendingUp className="h-3.5 w-3.5 text-accent" />
                  Popular Searches
                </p>
                <div className="flex flex-wrap gap-2">
                  {POPULAR_SEARCHES.map((term) => (
                    <button
                      key={term}
                      onClick={() => handleSelectSearch(term)}
                      className="inline-flex items-center gap-1 text-xs border border-border bg-secondary/20 px-3 py-1.5 rounded-full hover:border-primary hover:bg-primary/5 transition-colors"
                    >
                      <span>{term}</span>
                      <ArrowRight className="h-3 w-3 opacity-50" />
                    </button>
                  ))}
                </div>
              </div>

              {/* Recent Searches */}
              {recentSearches.length > 0 && (
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <p className="eyebrow flex items-center gap-1.5">
                      <History className="h-3.5 w-3.5 text-muted-foreground" />
                      Recent Searches
                    </p>
                    <button
                      onClick={() => {
                        setRecentSearches([]);
                        localStorage.removeItem(RECENT_KEY);
                      }}
                      className="text-[10px] text-muted-foreground hover:text-foreground uppercase tracking-wider"
                    >
                      Clear
                    </button>
                  </div>
                  <ul className="space-y-1">
                    {recentSearches.map((term) => (
                      <li key={term}>
                        <button
                          onClick={() => handleSelectSearch(term)}
                          className="w-full text-left py-2 px-2 text-xs flex items-center justify-between rounded hover:bg-secondary/40 transition-colors text-muted-foreground hover:text-foreground"
                        >
                          <span>{term}</span>
                          <ArrowRight className="h-3.5 w-3.5 opacity-40" />
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
